/**
 * BillingBrandGuard
 *
 * Full-screen overlay that blocks billing admin pages until a brand is selected.
 * Auto-selects when admin has exactly 1 brand (no overlay shown).
 *
 * @see US1, AC-1 in docs/specifications/2026-03-08-billing-admin-ui.md
 */
import React from "react";
import type { Brand } from "../../../types/brand";

interface BillingBrandGuardProps {
  children: React.ReactNode;
  isBrandRequired: boolean;
  brands: Brand[];
  isLoading: boolean;
  onSelectBrand: (brandId: string) => void;
}

const BillingBrandGuard: React.FC<BillingBrandGuardProps> = ({
  children,
  isBrandRequired,
  brands,
  isLoading,
  onSelectBrand,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isBrandRequired) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="h-6 w-6 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-neutral-900">
              Wybierz brand
            </h2>
            <p className="text-neutral-600 mt-2 text-sm">
              Wybierz brand, aby uzyskac dostep do panelu Billing Admin.
            </p>
          </div>
          <div className="space-y-2">
            {brands.map((brand) => (
              <button
                key={brand.id}
                onClick={() => onSelectBrand(brand.id)}
                className="w-full text-left px-4 py-3 rounded-lg border border-neutral-200 hover:border-primary-400 hover:bg-primary-50 transition-colors"
              >
                <div className="font-medium text-neutral-900">
                  {brand.displayName}
                </div>
                <div className="text-sm text-neutral-500">{brand.slug}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default BillingBrandGuard;
